<template>
  <bm-control v-bind="$attrs">
    <template #readOnly="{ dataQa }">
      <div v-if="imgSrc === null">-</div>
      <v-img
        v-else
        :src="'data:image;base64,' + imgSrc.fileContentsBase64"
        contain
        :max-width="imageWidth"
        :data-qa="dataQa"
      ></v-img>
    </template>

    <template #default="{ dataQa }">
      <v-file-input
        ref="imageUploader"
        class="mt-3"
        :value="placeholderFile"
        placeholder=" "
        color="brand02"
        :loading="loading"
        :rules="rules"
        :prepend-icon="bmIconName('AddPhotoAlternate')"
        :accept="fileTypes"
        :label="undefined"
        :data-qa="dataQa"
        :disabled="disabled"
        @change="newImage($event)"
      >
      </v-file-input>
      <v-img
        v-if="imgSrc !== null"
        :src="'data:image;base64,' + imgSrc.fileContentsBase64"
        contain
        :max-width="imageWidth"
      ></v-img>
    </template>
  </bm-control>
</template>

<script setup lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import Guid from "@/app/types/common/guid";
import { EnumFileType } from "@/app/services/generated/enums/enumFileType.generated";
import { FileBase64, fileService } from "@/app/services/generated/fileService";
import { ref, computed, watch } from "vue";
import { Base64 } from "@/app/base64";
import { acceptedImageTypes } from "@/app/imageUtils";
import messages, { keys } from "@/plugins/i18n";
import type { Maybe } from "@/components/utils/typeHelpers";
import { bmIconName } from "@/components/controls/bmIconMap";

interface BmImageUploaderByIdProps {
  value?: Guid | null;
  imageWidth?: number;
  maxFileMBytes?: number;
  resizeMaxFileBytes?: number;
  disabled?: boolean;
}

interface BmImageUploaderByIdEvents {
  (event: "update:value", value: Guid | null): void;
}

const props = withDefaults(defineProps<BmImageUploaderByIdProps>(), {
  imageWidth: 125,
  maxFileMBytes: 2,
  disabled: false,
});

const emit = defineEmits<BmImageUploaderByIdEvents>();

const imageUploader = ref<any>(null);
const imgSrc = ref<FileBase64 | null>(null);
const placeholderFile = ref<File | null>(null);
const loading = ref(false);
const fileTypes = acceptedImageTypes;

const rules = computed((): Function[] => {
  return [
    (value: File): boolean | string =>
      !value ||
      value.size <= props.maxFileMBytes * 1000000 ||
      messages.file_too_big + " " + props.maxFileMBytes + "MB",
    (value: File): boolean | string =>
      !value ||
      value.type === "image/internal" ||
      fileTypes.includes(value.type) ||
      messages.validation_incorrect_file_type,
  ];
});

watch(
  () => props.value ?? null,
  async (val) => {
    if (val !== null) {
      try {
        imgSrc.value = await fileService.getFileBase64(val);
      } catch (e: any) {
        throw Error(keys.product_image_retrieval_error + e);
      }

      if (imgSrc.value !== null) {
        // file with a name and no content. Allows file to appear on refresh and not trigger another upload
        placeholderFile.value = new File([], imgSrc.value.fileName, {
          type: "image/internal",
        });
      }
    } else {
      imgSrc.value = null;
      placeholderFile.value = null;
    }
    loading.value = false;
  },
  { immediate: true }
);

function newImage(file: Maybe<File>) {
  try {
    if (!file) {
      emit("update:value", null);
      return;
    }
    // Image internal is a file with a name and no contents to display in the uploader box.
    // Image internal is set to be able to clear the current image without triggering a change.
    if (file.type === "image/internal") {
      return;
    }

    if (!imageUploader.value?.validate()) {
      return;
    }

    loading.value = true;
    const fileReader = new FileReader();
    fileReader.onload = async () => {
      if (fileReader.result instanceof ArrayBuffer) {
        const fileUpload = new FileBase64();
        fileUpload.fileContentsBase64 = Base64.fromUint8Array(
          new Uint8Array(fileReader.result)
        );
        fileUpload.fileType = EnumFileType.image;
        fileUpload.id = Guid.create();
        fileUpload.fileName = file.name;
        await fileService.saveFileBase64(
          fileUpload,
          props.resizeMaxFileBytes ?? null
        );
        emit("update:value", fileUpload.id);

        loading.value = false;
        imgSrc.value = null;
        placeholderFile.value = null;
      } else {
        loading.value = false;
        throw Error(keys.product_upload_unexpected_file.value);
      }
    };
    fileReader.readAsArrayBuffer(file);
  } catch (e) {
    loading.value = false;
    throw Error(keys.product_upload_error + " " + e);
  }
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"

:deep(.v-input)
  @include bm-field
</style>
